import {createContext, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import axios, {AxiosError} from "axios";
import {ISale} from "../models/ISale.ts";
import {useNavigate} from "react-router-dom";

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL + '/api'

interface ISaleContextProps {
    sale: ISale | undefined,
    sales: ISale[] | undefined,
    setSelectedSaleId: (selectedSaleId: string | undefined) => void,
    createSaleMutation: any,
    createSaleError: AxiosError | null,
    updateSaleMutation: any,
    updateSaleError: AxiosError | null,
}

const SaleContext = createContext<ISaleContextProps>({} as ISaleContextProps);

interface ISaleContextProviderProps {
    children: React.ReactNode;
}

export const SaleProvider = ({children}: ISaleContextProviderProps) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient()
    const [createSaleError, setCreateSaleError] = useState<AxiosError | null>(null);
    const [updateSaleError, setUpdateSaleError] = useState<AxiosError | null>(null);
    const [selectedSaleId, setSelectedSaleId] = useState<string>();

    const {data: sales} = useQuery<ISale[]>({
        queryKey: ["sales"],
        retry: false,
        queryFn: async () => {
            const response = await axios.get(`${VITE_API_BASE_URL}/sale`, {withCredentials: true})
            return response.data
        },
    })

    const {data: sale} = useQuery<ISale>({
        queryKey: ["sale", selectedSaleId],
        enabled: !!selectedSaleId,
        retry: false,
        queryFn: async () => {
            const response = await axios.get(`${VITE_API_BASE_URL}/sale/${selectedSaleId}`, {withCredentials: true})
            return response.data
        }
    })

    const createSaleMutation = useMutation({
        mutationFn: async (sale: ISale) => {
            return axios.post(`${VITE_API_BASE_URL}/sale`, {sale}, {withCredentials: true});
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['sales']})
            navigate('/profile')
        },
        onError: async (error: AxiosError) => {
            setCreateSaleError(error)
        }
    })

    const updateSaleMutation = useMutation({
        mutationFn: async (sale: ISale) => {
            return axios.put(`${VITE_API_BASE_URL}/sale/${sale._id}`, sale, {withCredentials: true});
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['sale', selectedSaleId]})
            queryClient.invalidateQueries({queryKey: ['sales']})
            navigate('/profile')
        },
        onError: async (error: AxiosError) => {
            setUpdateSaleError(error)
        }
    })

    return (
        <SaleContext.Provider
            value={{
                sale,
                sales,
                setSelectedSaleId,
                createSaleMutation,
                updateSaleMutation,
                createSaleError,
                updateSaleError
            }}>
            {children}
        </SaleContext.Provider>
    )
}

export default SaleContext